// Anchor date for the rotating 2-day shift pattern — the first day of a
// shift-1 block. Mirrors models.ActiveShiftForDate in the Go backend
// exactly; keep the two in sync if this ever changes. Shifts alternate
// continuously every 2 days rather than resetting each calendar week, so a
// shift's weekdays drift over time — normal for a "2 on / 2 off" rotation.
export const SHIFT_EPOCH = new Date(2026, 7, 18);
export const SHIFT_BLOCK_DAYS = 2;

export const SHIFT_CLASSES = {1: 'shift-1', 2: 'shift-2', 3: 'shift-3'};
export const DAY_NAMES = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];

export function dateOnly(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function activeShiftForDate(date) {
  const diffDays = Math.round((dateOnly(date) - dateOnly(SHIFT_EPOCH)) / 86400000);
  const block = Math.floor(diffDays / SHIFT_BLOCK_DAYS);
  return (((block % 2) + 2) % 2 === 0) ? 1 : 2;
}

// "MieJoiVin" — fixed every Wednesday/Thursday/Friday, every week, no
// rotation. Mirrors models.ShiftMieJoiVin in the Go backend.
export const SHIFT_MIE_JOI_VIN = 3;

// Mirrors models.IsShiftActiveOnDate — whether a deliverer.shift value has
// them working on the given date. Shift 1/2 follow the rotating 2-day
// pattern; SHIFT_MIE_JOI_VIN is the fixed weekly Wed/Thu/Fri pattern.
export function isShiftActiveOnDate(shift, date) {
  if (shift === 1 || shift === 2) {
    return activeShiftForDate(date) === shift;
  }
  if (shift === SHIFT_MIE_JOI_VIN) {
    const day = date.getDay();
    return day === 3 || day === 4 || day === 5;
  }
  return false;
}

// Groups the /shift-changes response by deliverer id, oldest first.
export function groupShiftChanges(changes) {
  const byDeliverer = {};
  [...(changes || [])]
    .sort((a, b) => (a.effective_from < b.effective_from ? -1 : a.effective_from > b.effective_from ? 1 : a.id - b.id))
    .forEach((change) => {
      (byDeliverer[change.deliverer_id] = byDeliverer[change.deliverer_id] || []).push(change);
    });
  return byDeliverer;
}

// Mirrors models.ShiftHistory.ShiftOn — the shift a deliverer was on for a
// given date: the latest recorded change with effective_from <= date, so a
// future-dated change (a shift exchange) only applies from its date and past
// days keep the shift they actually had. Falls back to deliverer.shift.
export function shiftForDate(deliverer, date, shiftChangesByDeliverer) {
  const key = dateKey(date);
  let shift = null;
  (shiftChangesByDeliverer[deliverer.id] || []).forEach((change) => {
    if (change.effective_from.slice(0, 10) <= key) {
      shift = change.shift;
    }
  });
  return shift === null ? deliverer.shift : shift;
}

// Mirrors models.ShiftBlockStart — first day of the 2-day block date is in.
export function shiftBlockStart(date) {
  const diffDays = Math.round((dateOnly(date) - dateOnly(SHIFT_EPOCH)) / 86400000);
  const block = Math.floor(diffDays / SHIFT_BLOCK_DAYS);
  const start = dateOnly(SHIFT_EPOCH);
  start.setDate(start.getDate() + block * SHIFT_BLOCK_DAYS);
  return start;
}

// Preview of models.PlanShiftExchange (the backend recomputes and validates
// it): starting with the block containing date, whoever of the two is on
// shift in that block ("first") works its 2 days plus the next one covering
// "second"; "second" then works its remaining day plus first's next 2 days;
// from effectiveFrom they're on each other's old shift. Returns null when
// the pair isn't one shift-1 and one shift-2 deliverer on that date.
export function planShiftExchange(a, b, date, shiftChangesByDeliverer) {
  const blockStart = shiftBlockStart(date);
  const shiftA = shiftForDate(a, blockStart, shiftChangesByDeliverer);
  const shiftB = shiftForDate(b, blockStart, shiftChangesByDeliverer);
  if (!((shiftA === 1 && shiftB === 2) || (shiftA === 2 && shiftB === 1))) {
    return null;
  }
  const aFirst = activeShiftForDate(blockStart) === shiftA;
  const day = (offset) => {
    const d = new Date(blockStart);
    d.setDate(d.getDate() + offset);
    return d;
  };
  return {
    first: aFirst ? a : b,
    second: aFirst ? b : a,
    firstNewShift: aFirst ? shiftB : shiftA,
    secondNewShift: aFirst ? shiftA : shiftB,
    firstDays: [day(0), day(1), day(2)],
    secondDays: [day(3), day(4), day(5)],
    effectiveFrom: day(6),
  };
}

export const SHIFT_EXCHANGE_GROUP_PREFIX = 'shift-exchange-';

export function startOfWeek(date) {
  const d = dateOnly(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d;
}

export function formatDate(date) {
  const pad = (n) => `${n}`.padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

// YYYY-MM-DD, using local date components — matches what the backend expects
// and parses dates back into (see UpsertDelivererScheduleEntry).
export function dateKey(date) {
  const d = dateOnly(date);
  const pad = (n) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// "shift1"/"shift2"/"shift3" are computed on the fly from Deliverer.Shift,
// never stored. "normal"/"extra"/"extra_paid"/"vacation"/"swapped" are
// day-specific manual overrides persisted as DelivererScheduleEntry rows.
// Shared between Program.js (the editable scheduler) and Bonus.js (the
// read-only per-period bonus report), which both need to render the same
// per-day status a deliverer can have.
export const STATUS_LABELS = {
  shift1: 'Tura 1',
  shift2: 'Tura 2',
  shift3: 'MieJoiVin',
  normal: 'Program normal',
  extra: 'Extra',
  extra_paid: 'Extra (plătită)',
  vacation: 'Concediu',
  swapped: 'Schimb tură',
};

export const STATUS_CLASSES = {
  shift1: 'shift-1',
  shift2: 'shift-2',
  shift3: 'shift-3',
  normal: 'status-normal',
  extra: 'status-extra',
  extra_paid: 'status-extra-paid',
  vacation: 'status-vacation',
  swapped: 'status-swapped',
};

// Maps a Deliverer.Shift value to its computed-status key.
export const SHIFT_STATUS_KEYS = {1: 'shift1', 2: 'shift2', 3: 'shift3'};
